import { useEffect, useState, useCallback, useRef } from "react";
import { fmtDate } from "../lib/date";
import { useSearchParams } from "react-router-dom";
import api from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Input } from "../components/ui/input";
import { Button } from "../components/ui/button";
import { toast } from "sonner";
import { Vibrate, Plus, CheckCircle, Archive, ShieldWarning, WarningOctagon, Barcode, Lock, Question, Trash, ShieldCheck } from "@phosphor-icons/react";
import BatteriesTab from "./BatteriesTab";

const METHODS = [
  ["highest_verified", "Highest verified representative value"],
  ["manual_conservative", "Manually selected conservative value"],
  ["workplace_measurement", "Representative workplace measurement"],
  ["other", "Other verified evidence"],
];
const SOURCE_TYPES = [["manufacturer", "Manufacturer"], ["in_use", "In-use"], ["hse", "HSE"], ["other", "Other"]];
const POLICIES = [["", "Use organisation default"], ["allow", "Allow"], ["self_acknowledge", "Self-Acknowledge"], ["manager_override", "Manager Override"], ["hard_block", "Hard Block"]];
const POLICY_LABEL = { allow: "Allow", self_acknowledge: "Self-Acknowledge", manager_override: "Manager Override", hard_block: "Hard Block" };
const OVERRIDE_METHODS = [["either", "Either (point-of-use or advance)"], ["point_of_use", "Point-of-use only"], ["advance", "Advance only"], ["none", "No override (block)"]];
const ALERT_GROUPS = [["havs_manager", "HAVS Managers"], ["administrator", "Company Admins"], ["supervisor", "Supervisors"]];
const THRESHOLDS = [["warning", "Internal warning"], ["eav", "EAV (100 pts)"], ["approaching_elv", "Approaching ELV"], ["elv", "ELV (400 pts)"]];

function PointsBar({ points, eav, elv, warning }) {
  const pct = Math.min(100, (points / elv) * 100);
  const colour = points >= elv ? "bg-red-600" : points >= eav ? "bg-orange-500" : points >= (warning || 80) ? "bg-amber-400" : "bg-emerald-500";
  return (
    <div className="w-full">
      <div className="h-3 bg-slate-200 rounded-full overflow-hidden relative">
        <div className={`h-full ${colour} transition-all duration-300`} style={{ width: `${pct}%` }} />
        <div className="absolute top-0 h-full border-l-2 border-orange-700" style={{ left: `${(eav / elv) * 100}%` }} title="EAV" />
      </div>
      <div className="flex justify-between text-[10px] text-slate-500 mt-0.5"><span>0</span><span>EAV {eav}</span><span>ELV {elv}</span></div>
    </div>
  );
}

// ---------------------------------------------------------------- Manager override modal
function ManagerOverrideModal({ open, tool, missing, onCancel, onApprove }) {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  if (!open) return null;
  const submit = async () => {
    if (!email || !password || !reason.trim()) { toast.error("Manager email, password and reason are required"); return; }
    setBusy(true);
    await onApprove({ email, password, reason: reason.trim() });
    setBusy(false); setPassword("");
  };
  return (
    <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4" data-testid="havs-override-modal">
      <div className="bg-white rounded-lg max-w-md w-full p-5 shadow-2xl">
        <h3 className="font-display text-lg font-black text-slate-900 flex items-center gap-2"><Lock size={20} weight="bold" /> Manager approval required</h3>
        <p className="text-sm text-slate-600 mt-1">A manager must authorise use of <strong>{tool}</strong> using their own TMIC Ops account. An override is not a competency — it only records that a manager permitted this specific use.</p>
        {missing?.length > 0 && <div className="text-xs text-red-700 mt-2">{missing.map((m, i) => <div key={i}>• {m}</div>)}</div>}
        <div className="flex flex-col gap-2 mt-3">
          <Input data-testid="havs-override-email" placeholder="Manager email" value={email} onChange={(e) => setEmail(e.target.value)} className="h-10" />
          <Input data-testid="havs-override-password" type="password" placeholder="Manager password" value={password} onChange={(e) => setPassword(e.target.value)} className="h-10" />
          <Input data-testid="havs-override-reason" placeholder="Override reason (required)" value={reason} onChange={(e) => setReason(e.target.value)} className="h-10" />
        </div>
        <div className="flex gap-2 justify-end mt-4">
          <Button data-testid="havs-override-cancel" onClick={onCancel} variant="outline" className="h-10">Cancel</Button>
          <Button data-testid="havs-override-approve" onClick={submit} disabled={busy} className="h-10 bg-slate-900 font-semibold">{busy ? "Verifying…" : "Approve & log"}</Button>
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------- Eligibility banner
function EligibilityBanner({ elig }) {
  const [why, setWhy] = useState(false);
  if (!elig || elig.authorised) return null;
  const critical = elig.gate === "block";
  return (
    <div data-testid="havs-elig-banner" className={`rounded border-2 p-3 text-sm ${critical ? "border-red-400 bg-red-50 text-red-800" : "border-amber-400 bg-amber-50 text-amber-900"}`}>
      <div className="font-semibold flex items-center gap-1"><ShieldWarning weight="fill" /> {elig.message}</div>
      {elig.missing?.length > 0 && <div className="mt-1">{elig.missing.map((m, i) => <div key={i}>• {m}</div>)}</div>}
      <div className="text-xs mt-1">Policy: <strong>{POLICY_LABEL[elig.policy] || elig.policy}</strong></div>
      {elig.note && <div className="text-xs mt-1 italic">{elig.note}</div>}
      {(elig.requirements?.length > 0) && (
        <button data-testid="havs-why-toggle" onClick={() => setWhy((w) => !w)} className="mt-1 inline-flex items-center gap-1 text-xs font-semibold underline"><Question size={13} weight="bold" /> Why can't I do this?</button>
      )}
      {why && (
        <div className="mt-1 text-xs space-y-1">
          {elig.requirements.map((r, i) => (
            <div key={i} className={r.satisfied ? "text-emerald-700" : "text-red-700"}>
              {r.satisfied ? "✓" : "✗"} {r.label}{r.options?.length > 1 ? ` (any of: ${r.options.join(", ")})` : ""}{r.satisfied_by ? ` — held via ${r.satisfied_by}` : ` — ${r.state}`}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------- P2: LOG
function LogTab({ initialBattery }) {
  const [groups, setGroups] = useState([]);
  const [batteries, setBatteries] = useState([]);
  const [battery, setBattery] = useState(null);
  const [showAll, setShowAll] = useState(false);
  const [sel, setSel] = useState(null);
  const [elig, setElig] = useState(null);
  const [mins, setMins] = useState(10);
  const [other, setOther] = useState("");
  const [me, setMe] = useState(null);
  const [ack, setAck] = useState(false);
  const [ovOpen, setOvOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const busyRef = useRef(false);

  const loadMe = useCallback(() => api.get("/havs/me").then((r) => setMe(r.data)).catch(() => {}), []);
  const loadAllGroups = useCallback(() => api.get("/havs/tool-groups").then((r) => setGroups(r.data.filter((g) => g.assessment_magnitude != null && g.verification_status === "verified"))).catch(() => {}), []);
  useEffect(() => {
    loadAllGroups();
    api.get("/assets").then((r) => { const items = Array.isArray(r.data) ? r.data : r.data.items || []; setBatteries(items.filter((a) => a.battery_platform_id)); }).catch(() => {});
    loadMe();
  }, [loadMe, loadAllGroups]);

  const doScan = useCallback(async (id, all) => {
    try {
      const r = await api.get(`/havs/scan?asset_id=${id}${all ? "&show_all=true" : ""}`);
      setBattery(r.data);
      setGroups((r.data.tool_groups || []).filter((g) => g.assessment_magnitude != null && g.verification_status === "verified"));
    } catch (e) { toast.error("Scan failed"); }
  }, []);
  useEffect(() => { if (initialBattery) doScan(initialBattery, false); }, [initialBattery, doScan]);
  const chooseBattery = async (id) => {
    setSel(null); setElig(null); setAck(false); setShowAll(false);
    if (!id) { setBattery(null); loadAllGroups(); return; }
    await doScan(id, false);
  };
  const toggleShowAll = async () => { const v = !showAll; setShowAll(v); if (battery?.asset?.id) await doScan(battery.asset.id, v); };

  const selectGroup = async (g) => {
    setSel(g); setAck(false);
    if (!g) { setElig(null); return; }
    if (g.eligibility) { setElig(g.eligibility); return; }
    try { const r = await api.get(`/havs/eligibility/check?tool_group_id=${g.id}`); setElig(r.data); } catch (e) { setElig(null); }
  };

  const effMins = other ? Number(other) : mins;
  const projected = sel && effMins > 0 ? Math.round(2 * sel.assessment_magnitude ** 2 * (effMins / 60) * 10) / 10 : 0;

  const doLog = async (opts = {}) => {
    if (busyRef.current) return;          // guard against double-tap / duplicate submit
    busyRef.current = true; setBusy(true);
    const payload = { tool_group_id: sel.id, minutes: effMins, battery_asset_id: battery?.asset?.id || null };
    if (opts.acknowledge) payload.acknowledge = true;
    if (opts.override) { payload.override_manager_email = opts.override.email; payload.override_manager_password = opts.override.password; payload.override_reason = opts.override.reason; }
    try {
      const r = await api.post("/havs/log", payload);
      toast.success(`Logged +${r.data.session.points} pts`);
      setOther(""); setSel(null); setElig(null); setAck(false); setOvOpen(false); loadMe();
    } catch (e) {
      const d = e.response?.data?.detail;
      if (d?.code === "ack_required") { setAck(false); toast.error("Please acknowledge the warning to proceed"); }
      else if (d?.code === "override_required") { setOvOpen(true); }
      else if (d?.code === "blocked") { toast.error(d.message || "Blocked"); }
      else toast.error(typeof d === "string" ? d : (d?.message || "Failed"));
    } finally {
      busyRef.current = false; setBusy(false);
    }
  };

  const submit = () => {
    if (!sel) { toast.error("Choose a tool"); return; }
    if (!effMins || effMins <= 0) { toast.error("Enter trigger time"); return; }
    if (elig && !elig.authorised) {
      if (elig.gate === "block") { toast.error("You are not authorised to use this tool"); return; }
      if (elig.gate === "override") { setOvOpen(true); return; }
      if (elig.gate === "acknowledge") { if (!ack) { toast.error("Tick the acknowledgement to proceed"); return; } return doLog({ acknowledge: true }); }
    }
    doLog();
  };

  const cancelWarning = async () => {
    if (sel && elig && !elig.authorised) {
      try { await api.post("/havs/eligibility/cancel", { tool_group_id: sel.id, policy: elig.policy, missing: elig.missing, battery: battery ? `#${battery.asset.asset_number} ${battery.asset.name}` : null }); } catch (e) { /* noop */ }
    }
    setOvOpen(false);
  };

  const canLog = sel && effMins > 0 && !busy && !(elig && !elig.authorised && (elig.gate === "block"));

  return (
    <div className="flex flex-col gap-4 max-w-2xl">
      {me && (
        <div data-testid="havs-today" className={`rounded-lg p-4 border-2 ${me.status === "elv" ? "border-red-500 bg-red-50" : me.status === "eav" ? "border-orange-400 bg-orange-50" : me.status === "warning" ? "border-amber-400 bg-amber-50" : "border-slate-200 bg-white"}`}>
          <div className="flex items-baseline justify-between">
            <span className="font-body text-sm text-slate-600">HAVS Today</span>
            <span className="font-mono text-3xl font-black text-slate-900">{me.total_points} <span className="text-base font-normal text-slate-500">pts</span></span>
          </div>
          <div className="mt-2"><PointsBar points={me.total_points} eav={me.eav} elv={me.elv} warning={me.warning_points} /></div>
          <div className="text-xs text-slate-500 mt-1">A(8) ≈ {me.a8} m/s² · {me.total_points < me.eav ? `${Math.round(me.eav - me.total_points)} points until EAV` : "EAV reached — exposure control action required"}</div>
          {me.status === "elv" && <div className="mt-2 text-sm font-bold text-red-700 flex items-center gap-1"><WarningOctagon weight="fill" /> ELV EXCEEDED — stop and escalate immediately</div>}
          {me.status === "eav" && <div className="mt-2 text-sm font-bold text-orange-700 flex items-center gap-1"><ShieldWarning weight="fill" /> At/above EAV — exposure-control action required</div>}
          {me.status === "warning" && <div className="mt-2 text-sm font-bold text-amber-700">Approaching your internal warning threshold</div>}
          <div className="mt-3 space-y-1">
            {me.sessions.map((s) => (
              <div key={s.id} data-testid={`havs-session-${s.id}`} className="flex justify-between text-sm border-t border-slate-100 pt-1">
                <span className="text-slate-700">{s.tool_label}</span>
                <span className="text-slate-500">{s.duration_minutes} min · <strong className="text-slate-800">{s.points} pts</strong></span>
              </div>
            ))}
            {me.sessions.length === 0 && <div className="text-slate-400 text-sm">No tool use logged today.</div>}
          </div>
        </div>
      )}

      <div className="bg-white border-2 border-slate-200 rounded-lg p-4">
        <h3 className="font-body font-semibold text-slate-800 mb-2 flex items-center gap-1"><Barcode size={18} weight="bold" /> Log HAVS use</h3>
        <label className="text-xs font-semibold text-slate-600">Scan / select battery (optional)</label>
        <select data-testid="havs-log-battery" className="h-11 border-2 border-slate-300 rounded px-2 w-full mb-2" value={battery?.asset?.id || ""} onChange={(e) => chooseBattery(e.target.value)}>
          <option value="">No battery — manual entry</option>
          {batteries.map((b) => <option key={b.id} value={b.id}>#{b.asset_number} {b.name}</option>)}
        </select>
        {battery?.platform && (
          <div className="text-xs text-slate-600 mb-2 flex items-center justify-between">
            <span>Platform: <strong>{battery.platform.name}</strong> — showing relevant tools</span>
            <button data-testid="havs-showall" onClick={toggleShowAll} className="text-blue-700 font-semibold underline">{showAll ? "Show relevant only" : "Show all compatible tools"}</button>
          </div>
        )}
        <label className="text-xs font-semibold text-slate-600">Tool</label>
        <select data-testid="havs-log-group" className="h-11 border-2 border-slate-300 rounded px-2 w-full mb-2" value={sel?.id || ""} onChange={(e) => selectGroup(groups.find((g) => g.id === e.target.value) || null)}>
          <option value="">Choose tool…</option>
          {groups.map((g) => <option key={g.id} value={g.id}>{g.label} — {g.assessment_magnitude} m/s²{g.relevant === false ? " (other dept)" : ""}</option>)}
        </select>
        {groups.length === 0 && <p className="text-amber-700 text-sm mb-2">No tools with a verified assessment magnitude yet — set one up in Admin.</p>}

        {sel && <div className="mb-3"><EligibilityBanner elig={elig} /></div>}
        {sel && elig && !elig.authorised && elig.gate === "acknowledge" && (
          <label className="flex items-start gap-2 text-sm mb-3 text-amber-900" data-testid="havs-ack">
            <input type="checkbox" data-testid="havs-ack-checkbox" checked={ack} onChange={(e) => setAck(e.target.checked)} className="mt-0.5" />
            <span>I acknowledge I am not recorded as authorised. Completing this HAVS record does not authorise me to use the tool.</span>
          </label>
        )}

        <div className="flex gap-2 flex-wrap mb-3">
          {[5, 10, 15, 30].map((m) => (
            <button key={m} data-testid={`havs-mins-${m}`} onClick={() => { setMins(m); setOther(""); }}
              className={`px-4 py-2 rounded-full font-semibold text-sm border-2 ${!other && mins === m ? "bg-slate-900 text-white border-slate-900" : "bg-white text-slate-600 border-slate-300"}`}>{m} min</button>
          ))}
          <Input data-testid="havs-mins-other" placeholder="Other (min)" value={other} onChange={(e) => setOther(e.target.value.replace(/[^0-9.]/g, ""))} className="w-28 h-10" />
        </div>
        {sel && (
          <div data-testid="havs-projection" className="text-sm bg-blue-50 border border-blue-200 rounded px-3 py-2 mb-3 text-blue-800">
            Current: <strong>{me?.total_points ?? 0}</strong> pts · This use: <strong>≈ +{projected.toFixed(1)}</strong> pts · After: <strong>≈ {(((me?.total_points ?? 0) + projected)).toFixed(1)}</strong> pts
          </div>
        )}
        {sel && elig && !elig.authorised && elig.gate === "override"
          ? <Button data-testid="havs-log-override" onClick={() => setOvOpen(true)} disabled={busy} className="bg-slate-900 hover:bg-slate-800 font-semibold w-full h-11 disabled:opacity-50"><Lock size={16} weight="bold" className="mr-1" /> Get manager approval to log</Button>
          : <Button data-testid="havs-log-submit" onClick={submit} disabled={!canLog} className="bg-blue-600 hover:bg-blue-700 font-semibold w-full h-11 disabled:opacity-50">{busy ? "Logging…" : "Log trigger time"}</Button>}
      </div>

      <ManagerOverrideModal open={ovOpen} tool={sel?.label} missing={elig?.missing} onCancel={cancelWarning} onApprove={(o) => doLog({ override: o })} />
    </div>
  );
}

// ---------------------------------------------------------------- P1: ADMIN
function CatalogManager({ title, path, testid }) {
  const [rows, setRows] = useState([]);
  const [name, setName] = useState("");
  const load = useCallback(() => api.get(path).then((r) => setRows(r.data)).catch(() => {}), [path]);
  useEffect(() => { load(); }, [load]);
  const add = async () => { if (!name.trim()) return; try { await api.post(path, { name: name.trim() }); setName(""); load(); toast.success("Added"); } catch (e) { toast.error("Failed"); } };
  const archive = async (id) => { try { await api.post(`${path}/${id}/archive`); load(); } catch (e) { toast.error("Failed"); } };
  return (
    <div className="bg-white border-2 border-slate-200 rounded-lg p-4" data-testid={`havs-${testid}`}>
      <h3 className="font-body font-semibold text-slate-800 mb-2">{title}</h3>
      <div className="flex flex-wrap gap-1.5 mb-3">
        {rows.map((r) => (
          <span key={r.id} className="inline-flex items-center gap-1 text-xs bg-slate-100 rounded-full px-2 py-1">
            {r.name}
            <button data-testid={`havs-${testid}-archive-${r.id}`} onClick={() => archive(r.id)} className="text-slate-400 hover:text-red-600"><Archive size={12} weight="bold" /></button>
          </span>
        ))}
        {rows.length === 0 && <span className="text-slate-400 text-sm">None yet.</span>}
      </div>
      <div className="flex gap-2">
        <Input data-testid={`havs-${testid}-name`} placeholder="New name" value={name} onChange={(e) => setName(e.target.value)} onKeyDown={(e) => e.key === "Enter" && add()} className="h-9" />
        <Button data-testid={`havs-${testid}-add`} onClick={add} className="h-9 px-3 bg-slate-900 font-semibold"><Plus size={16} weight="bold" /></Button>
      </div>
    </div>
  );
}

// requirement (competency/authorisation) editor for a tool group
function GroupEligibility({ group, ctypes, onChanged }) {
  const [policy, setPolicy] = useState(group.operate_policy || "");
  const [method, setMethod] = useState(group.override_method || "either");
  const [note, setNote] = useState(group.eligibility_note || "");
  const [reqs, setReqs] = useState([]);
  const [label, setLabel] = useState("");
  const [picked, setPicked] = useState([]);
  const loadReqs = useCallback(() => api.get(`/requirements?owner_type=havs_tool_group&owner_id=${group.id}`).then((r) => setReqs(r.data)).catch(() => {}), [group.id]);
  useEffect(() => { loadReqs(); }, [loadReqs]);

  const savePolicy = async () => {
    try { await api.put(`/havs/tool-groups/${group.id}`, { operate_policy: policy || null, override_method: method, eligibility_note: note }); toast.success("Eligibility saved"); onChanged && onChanged(); } catch (e) { toast.error("Failed"); }
  };
  const addReq = async () => {
    if (picked.length === 0) { toast.error("Pick at least one competency/authorisation"); return; }
    try { await api.post("/requirements", { owner_type: "havs_tool_group", owner_id: group.id, kind: "qualification", label: label.trim() || null, competency_type_ids: picked }); setLabel(""); setPicked([]); loadReqs(); toast.success("Requirement added"); } catch (e) { toast.error("Failed"); }
  };
  const delReq = async (id) => { try { await api.delete(`/requirements/${id}`); loadReqs(); } catch (e) { toast.error("Failed"); } };

  return (
    <div className="mt-3 border-t border-slate-100 pt-3" data-testid={`havs-elig-editor-${group.id}`}>
      <div className="font-body font-semibold text-sm text-slate-800 mb-2 flex items-center gap-1"><ShieldCheck size={16} weight="bold" /> Operate eligibility</div>
      <div className="grid sm:grid-cols-2 gap-2 mb-2">
        <div>
          <label className="text-xs font-semibold text-slate-600">Operate policy</label>
          <select data-testid={`havs-policy-${group.id}`} className="h-9 border-2 border-slate-300 rounded px-2 w-full text-sm" value={policy} onChange={(e) => setPolicy(e.target.value)}>
            {POLICIES.map(([k, l]) => <option key={k} value={k}>{l}</option>)}
          </select>
        </div>
        {policy === "manager_override" && (
          <div>
            <label className="text-xs font-semibold text-slate-600">Override method</label>
            <select data-testid={`havs-ovmethod-${group.id}`} className="h-9 border-2 border-slate-300 rounded px-2 w-full text-sm" value={method} onChange={(e) => setMethod(e.target.value)}>
              {OVERRIDE_METHODS.map(([k, l]) => <option key={k} value={k}>{l}</option>)}
            </select>
          </div>
        )}
      </div>
      <Input data-testid={`havs-elignote-${group.id}`} placeholder="Optional explanation shown when not authorised" value={note} onChange={(e) => setNote(e.target.value)} className="h-9 mb-2" />
      <div className="mb-3">
        <div className="text-xs font-semibold text-slate-600 mb-1">Required competencies / authorisations</div>
        {reqs.length === 0 && <div className="text-xs text-slate-400 mb-1">None — anyone may operate (subject to policy).</div>}
        {reqs.map((r) => (
          <div key={r.id} className="flex items-center gap-2 text-sm bg-slate-50 rounded px-2 py-1 mb-1" data-testid={`havs-req-${r.id}`}>
            <span className="flex-1">{r.label || r.options.map((o) => o.name).join(" or ")}{r.options.length > 1 ? ` (any of: ${r.options.map((o) => o.name).join(", ")})` : ""}</span>
            <button data-testid={`havs-req-del-${r.id}`} onClick={() => delReq(r.id)} className="text-red-600"><Trash size={14} weight="bold" /></button>
          </div>
        ))}
        <div className="flex flex-col gap-1 mt-1">
          <Input data-testid={`havs-req-label-${group.id}`} placeholder="Requirement label (optional, e.g. 'Abrasive Wheels')" value={label} onChange={(e) => setLabel(e.target.value)} className="h-9" />
          <select multiple data-testid={`havs-req-comps-${group.id}`} className="border-2 border-slate-300 rounded px-2 py-1 text-sm h-24" value={picked} onChange={(e) => setPicked(Array.from(e.target.selectedOptions).map((o) => o.value))}>
            {ctypes.map((c) => <option key={c.id} value={c.id}>{c.name}{c.is_authorisation ? " (authorisation)" : ""}</option>)}
          </select>
          <span className="text-[11px] text-slate-400">Select multiple to allow equivalent qualifications to satisfy the same requirement.</span>
          <Button data-testid={`havs-req-add-${group.id}`} onClick={addReq} className="h-9 bg-slate-700 font-semibold text-sm self-start px-3"><Plus size={14} weight="bold" className="mr-1" /> Add requirement</Button>
        </div>
      </div>
      <Button data-testid={`havs-elig-save-${group.id}`} onClick={savePolicy} className="h-9 bg-blue-600 hover:bg-blue-700 font-semibold text-sm px-4">Save eligibility</Button>
    </div>
  );
}

function GroupEditor({ group, ctypes, onChanged }) {
  const [open, setOpen] = useState(false);
  const [models, setModels] = useState([]);
  const [mform, setMform] = useState({ manufacturer: "", model: "" });
  const [vform, setVform] = useState({});
  const loadModels = useCallback(() => api.get(`/havs/tool-groups/${group.id}/models`).then((r) => setModels(r.data)).catch(() => {}), [group.id]);
  useEffect(() => { if (open) loadModels(); }, [open, loadModels]);

  const verify = async () => { try { await api.post(`/havs/tool-groups/${group.id}/verify`); onChanged(); toast.success("Verified"); } catch (e) { toast.error("Failed"); } };
  const archive = async () => { try { await api.post(`/havs/tool-groups/${group.id}/archive`); onChanged(); } catch (e) { toast.error("Failed"); } };
  const addModel = async () => {
    if (!mform.model.trim()) return;
    try { await api.post("/havs/tool-models", { ...mform, tool_group_id: group.id }); setMform({ manufacturer: "", model: "" }); loadModels(); toast.success("Model added"); } catch (e) { toast.error("Failed"); }
  };
  const addValue = async (mid) => {
    const f = vform[mid] || {};
    if (!f.magnitude) { toast.error("Magnitude required"); return; }
    try { await api.post("/havs/vibration-values", { tool_model_id: mid, magnitude: Number(f.magnitude), source_type: f.source_type || "manufacturer", source_page: f.source_page }); setVform({ ...vform, [mid]: {} }); loadModels(); toast.success("Value added"); } catch (e) { toast.error("Failed"); }
  };
  const verifyValue = async (vid) => { try { const r = await api.post(`/havs/vibration-values/${vid}/verify`); loadModels(); onChanged(); toast.success(r.data.review_flagged ? "Verified — HAVS PROFILE REVIEW REQUIRED (exceeds assessment)" : "Verified"); } catch (e) { toast.error("Failed"); } };

  return (
    <div className="border border-slate-200 rounded-lg p-3" data-testid={`havs-group-${group.id}`}>
      <div className="flex items-center gap-2 flex-wrap">
        <span className="font-body font-semibold text-slate-800">{group.label}</span>
        <span className="font-mono text-sm text-slate-900">{group.assessment_magnitude != null ? `${group.assessment_magnitude} m/s²` : "no magnitude"}</span>
        <span className={`text-[10px] uppercase font-bold px-1.5 py-0.5 rounded ${group.verification_status === "verified" ? "bg-emerald-100 text-emerald-700" : "bg-amber-100 text-amber-700"}`}>{group.verification_status}</span>
        {group.operate_policy && <span className="text-[10px] uppercase font-bold px-1.5 py-0.5 rounded bg-slate-200 text-slate-700">{POLICY_LABEL[group.operate_policy]}</span>}
        {group.review_required && <span data-testid={`havs-review-${group.id}`} className="text-[10px] uppercase font-black px-1.5 py-0.5 rounded bg-red-600 text-white">HAVS PROFILE REVIEW REQUIRED</span>}
        <div className="ml-auto flex gap-2">
          {group.verification_status !== "verified" && <button data-testid={`havs-group-verify-${group.id}`} onClick={verify} className="text-emerald-700 font-bold text-sm inline-flex items-center gap-1"><CheckCircle size={15} weight="bold" />Verify</button>}
          <button data-testid={`havs-group-expand-${group.id}`} onClick={() => setOpen((o) => !o)} className="text-blue-700 font-bold text-sm">{open ? "Hide" : "Configure"}</button>
          <button data-testid={`havs-group-archive-${group.id}`} onClick={archive} className="text-red-600 font-bold text-sm"><Archive size={15} weight="bold" /></button>
        </div>
      </div>
      {group.assessment_reason && <div className="text-xs text-slate-500 mt-1">Reason: {group.assessment_reason}</div>}
      {open && (
        <>
          <GroupEligibility group={group} ctypes={ctypes} onChanged={onChanged} />
          <div className="mt-3 border-t border-slate-100 pt-3 space-y-3">
            <div className="font-body font-semibold text-sm text-slate-800">Models & vibration evidence</div>
            {models.map((m) => (
              <div key={m.id} className="bg-slate-50 rounded p-2" data-testid={`havs-model-${m.id}`}>
                <div className="font-body font-semibold text-sm text-slate-800">{m.manufacturer} {m.model}</div>
                {m.vibration_values.map((v) => (
                  <div key={v.id} className="flex items-center gap-2 text-sm mt-1">
                    <span className="font-mono">{v.magnitude} m/s²</span>
                    <span className="text-xs text-slate-500">{v.source_type}</span>
                    <span className={`text-[10px] uppercase font-bold px-1 rounded ${v.verification_status === "verified" ? "bg-emerald-100 text-emerald-700" : "bg-amber-100 text-amber-700"}`}>{v.verification_status}</span>
                    {v.verification_status !== "verified" && <button data-testid={`havs-value-verify-${v.id}`} onClick={() => verifyValue(v.id)} className="text-emerald-700 font-bold text-xs">Verify</button>}
                  </div>
                ))}
                <div className="flex gap-1 mt-2">
                  <Input data-testid={`havs-value-mag-${m.id}`} placeholder="m/s²" value={vform[m.id]?.magnitude || ""} onChange={(e) => setVform({ ...vform, [m.id]: { ...vform[m.id], magnitude: e.target.value } })} className="h-8 w-20" />
                  <select className="h-8 border-2 border-slate-300 rounded px-1 text-xs" value={vform[m.id]?.source_type || "manufacturer"} onChange={(e) => setVform({ ...vform, [m.id]: { ...vform[m.id], source_type: e.target.value } })}>
                    {SOURCE_TYPES.map(([k, l]) => <option key={k} value={k}>{l}</option>)}
                  </select>
                  <Button data-testid={`havs-value-add-${m.id}`} onClick={() => addValue(m.id)} className="h-8 px-2 bg-slate-700 text-xs font-semibold">Add value</Button>
                </div>
              </div>
            ))}
            <div className="flex gap-1">
              <Input placeholder="Manufacturer" value={mform.manufacturer} onChange={(e) => setMform({ ...mform, manufacturer: e.target.value })} className="h-9" data-testid={`havs-model-manu-${group.id}`} />
              <Input placeholder="Model" value={mform.model} onChange={(e) => setMform({ ...mform, model: e.target.value })} className="h-9" data-testid={`havs-model-model-${group.id}`} />
              <Button data-testid={`havs-model-add-${group.id}`} onClick={addModel} className="h-9 px-3 bg-slate-900 font-semibold"><Plus size={14} weight="bold" /></Button>
            </div>
          </div>
        </>
      )}
    </div>
  );
}

function AlertConfigEditor({ cfg, people, onChange }) {
  const set = (level, patch) => onChange({ ...cfg, [level]: { ...(cfg[level] || {}), ...patch } });
  const toggleGroup = (level, code) => {
    const cur = new Set((cfg[level]?.groups) || []);
    cur.has(code) ? cur.delete(code) : cur.add(code);
    set(level, { groups: Array.from(cur) });
  };
  return (
    <div className="space-y-2" data-testid="havs-alert-config">
      {THRESHOLDS.map(([lvl, lbl]) => {
        const c = cfg[lvl] || {};
        return (
          <div key={lvl} className="border border-slate-200 rounded p-2" data-testid={`havs-alert-${lvl}`}>
            <div className="font-body font-semibold text-sm text-slate-800">{lbl}</div>
            <div className="flex flex-wrap gap-3 mt-1">
              {ALERT_GROUPS.map(([code, name]) => (
                <label key={code} className="flex items-center gap-1 text-xs"><input type="checkbox" data-testid={`havs-alert-${lvl}-${code}`} checked={(c.groups || []).includes(code)} onChange={() => toggleGroup(lvl, code)} /> {name}</label>
              ))}
              <label className="flex items-center gap-1 text-xs"><input type="checkbox" data-testid={`havs-alert-${lvl}-supervisor`} checked={!!c.supervisor} onChange={(e) => set(lvl, { supervisor: e.target.checked })} /> Direct supervisor</label>
            </div>
            <select multiple data-testid={`havs-alert-${lvl}-people`} className="border-2 border-slate-200 rounded px-2 py-1 text-xs h-16 w-full mt-1" value={c.profile_ids || []} onChange={(e) => set(lvl, { profile_ids: Array.from(e.target.selectedOptions).map((o) => o.value) })}>
              {people.map((p) => <option key={p.id} value={p.id}>{p.first_name} {p.last_name}</option>)}
            </select>
          </div>
        );
      })}
    </div>
  );
}

function AdminTab() {
  const [platforms, setPlatforms] = useState([]);
  const [types, setTypes] = useState([]);
  const [groups, setGroups] = useState([]);
  const [ctypes, setCtypes] = useState([]);
  const [people, setPeople] = useState([]);
  const [warn, setWarn] = useState(80);
  const [approaching, setApproaching] = useState(300);
  const [defPolicy, setDefPolicy] = useState("allow");
  const [alerts, setAlerts] = useState(true);
  const [alertCfg, setAlertCfg] = useState({});
  const [gform, setGform] = useState({ battery_platform_id: "", tool_type_id: "", assessment_magnitude: "", assessment_method: "manual_conservative", assessment_reason: "" });

  const load = useCallback(() => {
    api.get("/havs/platforms").then((r) => setPlatforms(r.data)).catch(() => {});
    api.get("/havs/tool-types").then((r) => setTypes(r.data)).catch(() => {});
    api.get("/havs/tool-groups").then((r) => setGroups(r.data)).catch(() => {});
    api.get("/config/competency-types").then((r) => setCtypes(r.data)).catch(() => {});
    api.get("/people").then((r) => setPeople(r.data)).catch(() => {});
    api.get("/havs/settings").then((r) => { setWarn(r.data.warning_points); setApproaching(r.data.approaching_elv_points); setDefPolicy(r.data.default_operate_policy); setAlerts(r.data.exposure_alerts_enabled); setAlertCfg(r.data.alert_config || {}); }).catch(() => {});
  }, []);
  useEffect(() => { load(); }, [load]);

  const saveSettings = async () => {
    try { await api.put("/havs/settings", { warning_points: Number(warn), approaching_elv_points: Number(approaching), default_operate_policy: defPolicy, exposure_alerts_enabled: alerts, alert_config: alertCfg }); toast.success("Saved"); } catch (e) { toast.error("Failed"); }
  };
  const addGroup = async () => {
    if (!gform.tool_type_id) { toast.error("Choose a tool type"); return; }
    try {
      await api.post("/havs/tool-groups", { ...gform, assessment_magnitude: gform.assessment_magnitude ? Number(gform.assessment_magnitude) : null });
      setGform({ battery_platform_id: "", tool_type_id: "", assessment_magnitude: "", assessment_method: "manual_conservative", assessment_reason: "" });
      load(); toast.success("Tool group created");
    } catch (e) { toast.error("Failed"); }
  };

  return (
    <div className="flex flex-col gap-4">
      <div className="bg-white border-2 border-slate-200 rounded-lg p-4">
        <h3 className="font-body font-semibold text-slate-800 mb-2">Thresholds, default policy & alerts</h3>
        <div className="grid sm:grid-cols-3 gap-3 mb-3">
          <div><label className="text-xs font-semibold text-slate-600">Internal warning (pts)</label><Input data-testid="havs-warn-input" type="number" value={warn} onChange={(e) => setWarn(e.target.value)} className="h-10" /></div>
          <div><label className="text-xs font-semibold text-slate-600">Approaching ELV (pts)</label><Input data-testid="havs-approaching-input" type="number" value={approaching} onChange={(e) => setApproaching(e.target.value)} className="h-10" /></div>
          <div><label className="text-xs font-semibold text-slate-600">Default operate policy</label>
            <select data-testid="havs-defpolicy" className="h-10 border-2 border-slate-300 rounded px-2 w-full" value={defPolicy} onChange={(e) => setDefPolicy(e.target.value)}>
              {POLICIES.filter(([k]) => k).map(([k, l]) => <option key={k} value={k}>{l}</option>)}
            </select>
          </div>
        </div>
        <div className="text-[11px] text-slate-400 mb-2">EAV (100) and ELV (400) are protected statutory values and cannot be changed.</div>
        <label className="flex items-center gap-2 text-sm mb-3"><input type="checkbox" data-testid="havs-alerts-toggle" checked={alerts} onChange={(e) => setAlerts(e.target.checked)} /> Send exposure alerts on threshold crossings (no spam — once per crossing)</label>
        {alerts && <AlertConfigEditor cfg={alertCfg} people={people} onChange={setAlertCfg} />}
        <Button data-testid="havs-settings-save" onClick={saveSettings} className="h-10 px-4 bg-slate-900 font-semibold mt-3">Save settings</Button>
      </div>

      <div className="grid sm:grid-cols-2 gap-4">
        <CatalogManager title="Battery / tool platforms" path="/havs/platforms" testid="platforms" />
        <CatalogManager title="Tool types (reusable)" path="/havs/tool-types" testid="tooltypes" />
      </div>

      <div className="bg-white border-2 border-slate-200 rounded-lg p-4">
        <h3 className="font-body font-semibold text-slate-800 mb-2">Tool groups, assessment profiles & eligibility</h3>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-2 border-b border-slate-100 pb-3 mb-3">
          <select data-testid="havs-group-platform" className="h-10 border-2 border-slate-300 rounded px-2" value={gform.battery_platform_id} onChange={(e) => setGform({ ...gform, battery_platform_id: e.target.value })}>
            <option value="">Platform…</option>{platforms.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
          </select>
          <select data-testid="havs-group-tooltype" className="h-10 border-2 border-slate-300 rounded px-2" value={gform.tool_type_id} onChange={(e) => setGform({ ...gform, tool_type_id: e.target.value })}>
            <option value="">Tool type…</option>{types.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
          </select>
          <Input data-testid="havs-group-mag" placeholder="Assessment m/s²" value={gform.assessment_magnitude} onChange={(e) => setGform({ ...gform, assessment_magnitude: e.target.value })} className="h-10" />
          <select data-testid="havs-group-method" className="h-10 border-2 border-slate-300 rounded px-2 sm:col-span-2" value={gform.assessment_method} onChange={(e) => setGform({ ...gform, assessment_method: e.target.value })}>
            {METHODS.map(([k, l]) => <option key={k} value={k}>{l}</option>)}
          </select>
          <Input data-testid="havs-group-reason" placeholder="Reason / source for this value" value={gform.assessment_reason} onChange={(e) => setGform({ ...gform, assessment_reason: e.target.value })} className="h-10" />
          <Button data-testid="havs-group-add" onClick={addGroup} className="h-10 bg-blue-600 hover:bg-blue-700 font-semibold lg:col-span-3"><Plus size={16} weight="bold" className="mr-1" />Create tool group</Button>
        </div>
        <div className="space-y-2">
          {groups.map((g) => <GroupEditor key={g.id} group={g} ctypes={ctypes} onChanged={load} />)}
          {groups.length === 0 && <p className="text-slate-400 text-sm">No tool groups yet. Create one above, then add eligibility, models & vibration evidence.</p>}
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------- P3: REPORTS
function PersonDetail({ pid, onClose }) {
  const [data, setData] = useState(null);
  useEffect(() => { api.get(`/havs/reports/person/${pid}?days=14`).then((r) => setData(r.data)).catch(() => {}); }, [pid]);
  const badge = (s) => s === "elv" ? "bg-red-600 text-white" : s === "eav" ? "bg-orange-500 text-white" : s === "warning" ? "bg-amber-400 text-slate-900" : "bg-emerald-500 text-white";
  return (
    <div className="bg-white border-2 border-blue-300 rounded-lg p-4" data-testid="havs-person-detail">
      <div className="flex items-center justify-between mb-2">
        <h3 className="font-body font-semibold text-slate-800">HAVS exposure detail</h3>
        <button data-testid="havs-person-close" onClick={onClose} className="text-slate-400 hover:text-slate-700 text-sm font-semibold">Close</button>
      </div>
      {data?.history?.length ? data.history.map((d) => (
        <div key={d.date} className="border-t border-slate-100 py-2">
          <div className="flex items-center gap-2 text-sm">
            <span className="font-body font-semibold text-slate-800">{fmtDate(d.date)}</span>
            <span className="font-mono font-bold ml-auto">{d.points} pts</span>
            <span className="text-slate-500 text-xs">A(8) {d.a8}</span>
            <span className={`text-[10px] uppercase font-bold px-1.5 py-0.5 rounded ${badge(d.status)}`}>{d.status}</span>
          </div>
          {d.sessions.map((s) => <div key={s.id} className="flex justify-between text-xs text-slate-500 pl-2 mt-0.5"><span>{s.tool_label}</span><span>{s.duration_minutes} min · {s.points} pts</span></div>)}
        </div>
      )) : <p className="text-slate-400 text-sm">No exposure recorded.</p>}
    </div>
  );
}

function ReportsTab({ initialPerson }) {
  const [today, setToday] = useState(null);
  const [depts, setDepts] = useState(null);
  const [person, setPerson] = useState(initialPerson || null);
  useEffect(() => {
    api.get("/havs/reports/today").then((r) => setToday(r.data)).catch(() => {});
    api.get("/havs/reports/departments").then((r) => setDepts(r.data)).catch(() => {});
  }, []);
  useEffect(() => { if (initialPerson) setPerson(initialPerson); }, [initialPerson]);
  const badge = (s) => s === "elv" ? "bg-red-600 text-white" : s === "eav" ? "bg-orange-500 text-white" : s === "warning" ? "bg-amber-400 text-slate-900" : "bg-emerald-500 text-white";
  return (
    <div className="flex flex-col gap-4">
      {person && <PersonDetail pid={person} onClose={() => setPerson(null)} />}
      <div className="bg-white border-2 border-slate-200 rounded-lg p-4">
        <h3 className="font-body font-semibold text-slate-800 mb-2">Today's exposure by person</h3>
        {today?.people?.length ? today.people.map((p) => (
          <button key={p.profile_id} data-testid={`havs-report-person-${p.profile_id}`} onClick={() => setPerson(p.profile_id)} className="flex items-center gap-2 border-t border-slate-100 py-1.5 text-sm w-full text-left hover:bg-slate-50">
            <span className="flex-1 font-body text-slate-800">{p.name}</span>
            <span className="text-slate-500 text-xs">A(8) {p.a8}</span>
            <span className="font-mono font-bold">{p.points} pts</span>
            <span className={`text-[10px] uppercase font-bold px-1.5 py-0.5 rounded ${badge(p.status)}`}>{p.status}</span>
          </button>
        )) : <p className="text-slate-400 text-sm">No exposure logged today.</p>}
      </div>
      <div className="bg-white border-2 border-slate-200 rounded-lg p-4">
        <h3 className="font-body font-semibold text-slate-800 mb-2">Department overview (today)</h3>
        {depts?.departments?.length ? depts.departments.map((d) => (
          <div key={d.department_id || "none"} data-testid={`havs-report-dept-${d.department_id || "none"}`} className="flex items-center gap-3 border-t border-slate-100 py-1.5 text-sm">
            <span className="flex-1 font-body font-semibold text-slate-800">{d.department}</span>
            <span className="text-slate-500 text-xs">{d.people} people</span>
            {d.approaching > 0 && <span className="text-amber-700 text-xs font-bold">{d.approaching} approaching</span>}
            {d.at_eav > 0 && <span className="text-orange-700 text-xs font-bold">{d.at_eav} at EAV</span>}
            {d.at_elv > 0 && <span className="text-red-700 text-xs font-bold">{d.at_elv} at ELV</span>}
          </div>
        )) : <p className="text-slate-400 text-sm">No department exposure today.</p>}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------- P3: HEALTH (restricted)
const HEALTH_STATUS = ["not_enrolled", "enrolled", "assessment_due", "under_review", "current", "restricted"];
function HealthTab() {
  const [people, setPeople] = useState([]);
  const [sel, setSel] = useState(null);
  const [rec, setRec] = useState(null);
  useEffect(() => { api.get("/people").then((r) => setPeople(r.data)).catch(() => {}); }, []);
  const openPerson = async (p) => {
    setSel(p);
    try { const r = await api.get(`/havs/health/${p.id}`); setRec(r.data); } catch (e) { toast.error(e.response?.data?.detail || "No access"); }
  };
  const save = async () => {
    try { await api.put(`/havs/health/${sel.id}`, rec); toast.success("Saved"); } catch (e) { toast.error("Failed"); }
  };
  return (
    <div className="grid md:grid-cols-[260px_1fr] gap-4">
      <div className="bg-white border-2 border-slate-200 rounded-lg p-3">
        <h3 className="font-body font-semibold text-slate-800 text-sm mb-2">People</h3>
        {people.map((p) => (
          <button key={p.id} data-testid={`havs-health-person-${p.id}`} onClick={() => openPerson(p)} className={`block w-full text-left px-3 py-2 rounded text-sm ${sel?.id === p.id ? "bg-slate-900 text-white" : "hover:bg-slate-100 text-slate-700"}`}>{p.first_name} {p.last_name}</button>
        ))}
      </div>
      <div className="bg-white border-2 border-slate-200 rounded-lg p-4">
        {sel && rec ? (
          <div className="flex flex-col gap-3" data-testid="havs-health-form">
            <h3 className="font-display text-lg font-black text-slate-900">{sel.first_name} {sel.last_name} — Health Surveillance</h3>
            <p className="text-xs text-amber-700 font-semibold">Administrative status only — do NOT enter medical/clinical information.</p>
            <label className="flex items-center gap-2 text-sm"><input type="checkbox" data-testid="havs-health-required" checked={!!rec.surveillance_required} onChange={(e) => setRec({ ...rec, surveillance_required: e.target.checked })} /> Surveillance required</label>
            <div><label className="text-xs font-semibold text-slate-600">Status</label>
              <select data-testid="havs-health-status" className="h-10 border-2 border-slate-300 rounded px-2 w-full" value={rec.status || "not_enrolled"} onChange={(e) => setRec({ ...rec, status: e.target.value })}>
                {HEALTH_STATUS.map((s) => <option key={s} value={s}>{s.replace(/_/g, " ")}</option>)}
              </select>
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div><label className="text-xs font-semibold text-slate-600">Last assessment</label><Input type="date" value={rec.last_assessment_date || ""} onChange={(e) => setRec({ ...rec, last_assessment_date: e.target.value })} /></div>
              <div><label className="text-xs font-semibold text-slate-600">Next due</label><Input data-testid="havs-health-nextdue" type="date" value={rec.next_due_date || ""} onChange={(e) => setRec({ ...rec, next_due_date: e.target.value })} /></div>
            </div>
            <div><label className="text-xs font-semibold text-slate-600">Provider / reference</label><Input value={rec.provider_ref || ""} onChange={(e) => setRec({ ...rec, provider_ref: e.target.value })} /></div>
            <label className="flex items-center gap-2 text-sm"><input type="checkbox" data-testid="havs-health-restriction" checked={!!rec.work_restriction} onChange={(e) => setRec({ ...rec, work_restriction: e.target.checked })} /> Work restriction present</label>
            {rec.work_restriction && <Input placeholder="Minimal work-management summary (no medical reason)" value={rec.restriction_summary || ""} onChange={(e) => setRec({ ...rec, restriction_summary: e.target.value })} />}
            <div><label className="text-xs font-semibold text-slate-600">Administrative notes (no clinical info)</label><Input value={rec.admin_notes || ""} onChange={(e) => setRec({ ...rec, admin_notes: e.target.value })} /></div>
            <Button data-testid="havs-health-save" onClick={save} className="bg-blue-600 hover:bg-blue-700 font-semibold self-start">Save</Button>
          </div>
        ) : <p className="text-slate-400 text-sm">Select a person. Access is logged.</p>}
      </div>
    </div>
  );
}

export default function HavsPage() {
  const { hasPerm } = useAuth();
  const [params] = useSearchParams();
  const canManage = hasPerm("havs_manager", "administrator");
  const canView = hasPerm("havs_manager", "supervisor", "administrator");
  const canHealth = hasPerm("health_admin", "administrator");
  const deepPerson = params.get("person");
  const deepBattery = params.get("battery");
  const [tab, setTab] = useState(deepBattery ? "log" : (deepPerson && canView ? "reports" : "log"));
  const tabs = [["log", "Log Use"]];
  if (canManage) tabs.push(["batteries", "Batteries"]);
  if (canManage) tabs.push(["admin", "Admin"]);
  if (canView) tabs.push(["reports", "Reports"]);
  if (canHealth) tabs.push(["health", "Health Surveillance"]);

  return (
    <div className="flex flex-col gap-5">
      <div>
        <h1 className="font-display text-3xl font-black tracking-tight text-slate-900 flex items-center gap-2"><Vibrate size={28} weight="bold" /> HAVS</h1>
        <p className="font-body text-slate-600 text-sm">Hand-Arm Vibration exposure logging & management. Assists risk assessment and record-keeping — logging exposure does not by itself establish legal compliance or authorise tool use.</p>
      </div>
      <div className="flex gap-2 border-b border-slate-200 flex-wrap">
        {tabs.map(([k, l]) => (
          <button key={k} data-testid={`havs-tab-${k}`} onClick={() => setTab(k)}
            className={`px-4 py-2 font-body font-semibold text-sm -mb-px border-b-2 ${tab === k ? "border-slate-900 text-slate-900" : "border-transparent text-slate-500"}`}>{l}</button>
        ))}
      </div>
      {tab === "log" && <LogTab initialBattery={deepBattery} />}
      {tab === "batteries" && canManage && <BatteriesTab />}
      {tab === "admin" && canManage && <AdminTab />}
      {tab === "reports" && canView && <ReportsTab initialPerson={deepPerson} />}
      {tab === "health" && canHealth && <HealthTab />}
    </div>
  );
}
